import React, { useState, useEffect } from 'react';
import './action_orglevel.css';
import axios from 'axios';
import * as XLSX from 'xlsx';
import DataTable, { ActionMenu } from './DataTable';
import { Icons } from './ActionIcons';
import ProgressBar from './ProgressBar';

const ActionOrgLevel: React.FC = () => {
  // theme handled globally in AppLayout

  const [allItems, setAllItems] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(Number(localStorage.getItem('action_orglevel_pageSize') || '50'));
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const [sortBy, setSortBy] = useState<string>('code');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<any | null>(null);

  const loadAllData = async () => {
    if (loading) return;
    setLoading(true);
    setLoadingProgress({ current: 0, total: 0 });
    try {
      const base = process.env.REACT_APP_API_URL || '';
      const urlBase = base ? base.replace(/\/$/, '') : '';
      const allData: any[] = [];
      let currentPage = 1;
      const fetchPageSize = 5000;
      let totalItems = 0;
      
      const firstRes = await axios.get(`${urlBase}/api/sap-actions-orglevel?page=1&pageSize=${fetchPageSize}`, { withCredentials: true });
      totalItems = firstRes.data.total || 0;
      allData.push(...(firstRes.data.items || []));
      setLoadingProgress({ current: allData.length, total: totalItems });
      console.log(`Total: ${totalItems}, primeira página: ${allData.length}`);
      
      currentPage = 2;
      while (allData.length < totalItems) {
        const res = await axios.get(`${urlBase}/api/sap-actions-orglevel?page=${currentPage}&pageSize=${fetchPageSize}`, { withCredentials: true });
        const items = res.data.items || [];
        if (items.length === 0) break;
        allData.push(...items);
        setLoadingProgress({ current: allData.length, total: totalItems });
        console.log(`Página ${currentPage}: ${allData.length}/${totalItems}`);
        currentPage++;
      }
      
      setAllItems(allData);
      console.log(`✓ Dados carregados: ${allData.length} itens`);
    } catch (err) {
      console.error('Erro carregar todos os dados', err);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (allItems.length === 0) {
      loadAllData();
    }
    // loadAllData is intentionally excluded to avoid looped reloads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtrar dados no frontend
  const filteredItems = allItems.filter(item => {
    return Object.entries(columnFilters).every(([column, filterValue]) => {
      if (!filterValue) return true;
      const itemValue = String(item[column] || '').toLowerCase();
      return itemValue.includes(filterValue.toLowerCase());
    });
  });

  // Ordenar dados filtrados
  const sortedItems = [...filteredItems].sort((a, b) => {
    const aVal = String(a[sortBy] || '');
    const bVal = String(b[sortBy] || '');
    const comparison = aVal.localeCompare(bVal);
    return sortDir === 'asc' ? comparison : -comparison;
  });

  // Atualizar total com base nos itens filtrados
  const totalFiltered = sortedItems.length;

  // Paginar dados ordenados
  const paginatedItems = sortedItems.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const displayedItems = paginatedItems;

  return (
    <>
  <div className="container py-5" style={{ marginTop: 80, paddingBottom: 120 }}>
        <h1>Ações x Nível organizacional</h1>
        <p className="lead">Repositório relacionando ações com níveis organizacionais.</p>
        <p>Faça upload de um arquivo .xlsx com colunas: code, orglevel, orgleveldescription para popular a tabela.</p>

        <div style={{ marginBottom: 16 }}>
          <input type="file" accept=".xlsx,.xls" onChange={async (e) => {
            const f = e.target.files && e.target.files[0];
            if (!f) return;
            try {
              const data = await f.arrayBuffer();
              const wb = XLSX.read(data, { type: 'array' });
              const first = wb.Sheets[wb.SheetNames[0]];
              const json: any[] = XLSX.utils.sheet_to_json(first, { defval: '' });
              // expected columns: code, orglevel, orgleveldescription
              const items = json.map(r => ({ code: r.code || r.CODE || r.Codigo || r.Código || r['Código'] || '', orglevel: r.orglevel || r.ORGLEVEL || r['org level'] || '', orgleveldescription: r.orgleveldescription || r.orglevelDescription || r['orgleveldescription'] || r['org level description'] || '' })).filter(it => it.code);
              if (items.length === 0) { alert('Nenhum registro válido encontrado no arquivo. A coluna code é obrigatória.'); return; }
              // send to backend
              const base = process.env.REACT_APP_API_URL || '';
              const urlBase = base ? base.replace(/\/$/, '') : '';
              const res = await axios.post(`${urlBase}/api/sap-actions-orglevel/bulk`, { items }, { withCredentials: true });
              if (res && res.data) {
                alert(`Import concluído: recebidos=${res.data.received} inseridos=${res.data.inserted} duplicados=${res.data.duplicates}`);
                // refresh list
                loadAllData();
              }
            } catch (err) {
              console.error('Erro importar xlsx', err);
              alert('Erro ao importar arquivo. Veja console.');
            }
          }} />
        </div>

        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-outline-danger" onClick={async () => {
            if (selectedIds.size === 0) { alert('Nenhum item selecionado'); return; }
            if (!window.confirm(`Confirma exclusão de ${selectedIds.size} item(s)?`)) return;
            try {
              const base = process.env.REACT_APP_API_URL || '';
              const urlBase = base ? base.replace(/\/$/, '') : '';
              const res = await axios.delete(`${urlBase}/api/sap-actions-orglevel/bulk`, { data: { ids: Array.from(selectedIds) }, withCredentials: true });
              if (res && res.data) {
                alert(`Excluídos: ${res.data.deletedCount || 0}`);
                // Remover itens localmente ao invés de recarregar tudo
                const idsArray = Array.from(selectedIds);
                setAllItems(prev => prev.filter(item => !idsArray.includes(item._id)));
                setSelectedIds(new Set());
              }
            } catch (err) { console.error('Erro excluir selecionados', err); alert('Erro ao excluir. Veja console.'); }
          }}>Excluir selecionados</button>
        </div>

        {loading && loadingProgress.total > 0 && (
          <ProgressBar 
            current={loadingProgress.current} 
            total={loadingProgress.total}
            message="Carregando ações x nível organizacional..."
          />
        )}
        {loading && loadingProgress.total === 0 && (
          <div className="text-center py-3">Carregando...</div>
        )}
        {!loading && (
          <DataTable
          columns={[
            {
              key: '_select',
              label: 'Seleção',
              minWidth: 50,
              sortable: false,
              checkboxColumn: true,
              allChecked: displayedItems.length > 0 && displayedItems.every(item => item._id && selectedIds.has(item._id)),
              onCheckboxHeaderClick: () => {
                const allSelected = displayedItems.every(item => item._id && selectedIds.has(item._id));
                if (allSelected) {
                  setSelectedIds(new Set());
                } else {
                  setSelectedIds(new Set(displayedItems.map(i => i._id).filter(Boolean)));
                }
              },
              render: (value: any, item: any) => (
                <input 
                  type="checkbox" 
                  checked={item._id ? selectedIds.has(item._id) : false}
                  onChange={() => {
                    const ns = new Set(selectedIds);
                    if (item._id && ns.has(item._id)) ns.delete(item._id);
                    else if (item._id) ns.add(item._id);
                    setSelectedIds(ns);
                  }}
                  style={{ cursor: 'pointer' }}
                />
              )
            },
            {
              key: 'code',
              label: 'Código',
              minWidth: 120,
              sortable: true,
              render: (value: any, item: any) => <span style={{ fontWeight: 500 }}>{item.code}</span>
            },
            {
              key: 'orglevel',
              label: 'Nível organizacional',
              minWidth: 180,
              sortable: true,
              render: (value: any, item: any) => <span>{item.orglevel || ''}</span>
            },
            {
              key: 'orgleveldescription',
              label: 'Descrição',
              minWidth: 200,
              sortable: true,
              render: (value: any, item: any) => <span>{item.orgleveldescription || ''}</span>
            },
            {
              key: 'actions',
              label: 'Ações',
              minWidth: 100,
              sortable: false,
              render: (value: any, item: any) => (
                <ActionMenu
                  actions={[
                    {
                      key: 'edit',
                      label: 'Editar',
                      icon: Icons.Pencil,
                      onClick: () => setEditing(item)
                    },
                    {
                      key: 'delete',
                      label: 'Excluir',
                      icon: Icons.Trash,
                      variant: 'danger',
                      onClick: async () => {
                        if (!window.confirm('Confirma exclusão deste item?')) return;
                        try {
                          const base = process.env.REACT_APP_API_URL || '';
                          const urlBase = base ? base.replace(/\/$/, '') : '';
                          const res = await axios.delete(`${urlBase}/api/sap-actions-orglevel/bulk`, { data: { ids: [item._id] }, withCredentials: true });
                          if (res && res.data) {
                            // Remover item localmente ao invés de recarregar tudo
                            setAllItems(prev => prev.filter(i => i._id !== item._id));
                            alert('Excluído');
                          }
                        } catch (err) { console.error('Erro excluir', err); alert('Erro ao excluir'); }
                      }
                    }
                  ]}
                />
              )
            }
          ]}
          data={displayedItems}
          keyExtractor={(item: any) => item._id || item.code}

          sortColumn={sortBy}
          sortDirection={sortDir}
          onSort={(column: string, direction?: 'asc' | 'desc') => {
            if (direction) {
              setSortBy(column);
              setSortDir(direction);
            } else if (sortBy === column) {
              setSortDir(d => d === 'asc' ? 'desc' : 'asc');
            } else {
              setSortBy(column);
              setSortDir('asc');
            }
            setPage(1);
          }}
          onFilter={(columnKey: string, value: string) => {
            setColumnFilters(prev => ({ ...prev, [columnKey]: value }));
            setPage(1);
          }}
          filters={columnFilters}
          striped
          hover
          responsive
          emptyMessage="Nenhum item disponível"
          pagination={true}
          currentPage={page}
          pageSize={pageSize}
          totalItems={totalFiltered}
          onPageChange={(newPage: number) => {
            setPage(newPage);
          }}
          onPageSizeChange={(size: number) => {
            setPageSize(size);
            setPage(1);
            localStorage.setItem('action_orglevel_pageSize', String(size));
          }}
          pageSizeOptions={[10, 25, 50, 100, 200]}
        />
        )}

        {/* Edit modal */}
        {editing && (
          <div className="users-overlay">
            <div className="users-overlay-box" onClick={e => e.stopPropagation()}>
              <button className="modal-close-btn" aria-label="Fechar" onClick={() => setEditing(null)}>×</button>
              <h4>Editar {editing.code}</h4>
              <div className="mb-2">
                <label className="form-label">Código</label>
                <input className="form-control" value={editing.code || ''} onChange={e => setEditing({ ...editing, code: e.target.value })} />
              </div>
              <div className="mb-2">
                <label className="form-label">Nível organizacional</label>
                <input className="form-control" value={editing.orglevel || ''} onChange={e => setEditing({ ...editing, orglevel: e.target.value })} />
              </div>
              <div className="mb-3">
                <label className="form-label">Descrição nível</label>
                <input className="form-control" value={editing.orgleveldescription || ''} onChange={e => setEditing({ ...editing, orgleveldescription: e.target.value })} />
              </div>
              <div className="d-flex gap-2 justify-content-end">
                <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancelar</button>
                <button className="btn btn-primary" onClick={async () => {
                  try {
                    const base = process.env.REACT_APP_API_URL || '';
                    const urlBase = base ? base.replace(/\/$/, '') : '';
                    const id = editing._id;
                    const payload = { code: editing.code, orglevel: editing.orglevel, orgleveldescription: editing.orgleveldescription };
                    const res = await axios.patch(`${urlBase}/api/sap-actions-orglevel/${id}`, payload, { withCredentials: true });
                    if (res && res.data) {
                      // Atualizar item localmente ao invés de recarregar tudo
                      setAllItems(prev => prev.map(item => 
                        item._id === id ? { ...item, ...payload } : item
                      ));
                      setEditing(null);
                      alert('Atualizado');
                    }
                  } catch (err) { console.error('Erro salvar edição', err); alert('Erro salvar edição'); }
                }}>Salvar</button>
              </div>
            </div>
          </div>
        )}


      </div>
    </>
  );
};

export default ActionOrgLevel;
